import { ChevronDown, ChevronRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink } from 'react-router-dom'

import { cn } from '@/lib/cn'
import type { NavItemLink, NavSection } from '@/config/navModel'

export interface SidebarNavMenuProps {
  section: NavSection
  showLabels: boolean
  /** `to` of the nav item matching the current route, if any. */
  activeItemTo?: string
  isOpen: boolean
  onToggle: () => void
  unreadCount: number
  baseLinkClass: string
  activeClass: string
  inactiveClass: string
  childLinkClass: string
  childInactiveClass: string
  childActiveClass: string
}

const FLYOUT_CLOSE_DELAY_MS = 120

/**
 * One sidebar menu: a row that expands to its pages, like Companies. A menu with a
 * single visible page renders as a plain link, and the collapsed (icon-only)
 * sidebar shows one icon per menu with its pages in a hover flyout.
 */
export function SidebarNavMenu({
  section,
  showLabels,
  activeItemTo,
  isOpen,
  onToggle,
  unreadCount,
  baseLinkClass,
  activeClass,
  inactiveClass,
  childLinkClass,
  childInactiveClass,
  childActiveClass,
}: SidebarNavMenuProps): React.ReactElement {
  const containsActive = section.items.some((item) => item.to === activeItemTo)
  const MenuIcon = section.icon ?? section.items[0].icon
  const submenuId = `sidebar-menu-${section.label.replace(/\W+/g, '-').toLowerCase()}`

  const triggerRef = useRef<HTMLAnchorElement>(null)
  const closeTimer = useRef<number | null>(null)
  const [flyoutTop, setFlyoutTop] = useState<number | null>(null)
  const [flyoutLeft, setFlyoutLeft] = useState(0)

  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    },
    []
  )

  const openFlyout = (): void => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    setFlyoutLeft(rect.right + 8)
    setFlyoutTop(rect.top)
  }
  const closeFlyoutSoon = (): void => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setFlyoutTop(null), FLYOUT_CLOSE_DELAY_MS)
  }

  const renderLink = (item: NavItemLink, asChild: boolean): React.ReactElement => {
    const { to, label, icon: Icon } = item
    const isActive = to === activeItemTo
    return (
      <NavLink
        key={to}
        to={to}
        end
        title={label}
        className={cn(
          asChild ? childLinkClass : baseLinkClass,
          asChild
            ? isActive
              ? childActiveClass
              : childInactiveClass
            : isActive
              ? activeClass
              : inactiveClass
        )}
      >
        {!asChild && <Icon className="relative z-[1] h-[18px] w-[18px] shrink-0" />}
        <span
          className={cn(
            'min-w-0 flex-1 overflow-hidden whitespace-nowrap transition-all duration-200',
            !asChild && !showLabels && 'max-w-0 opacity-0'
          )}
        >
          {label}
        </span>
        {to === '/notifications' && unreadCount > 0 && (
          <span
            className={cn(
              'relative z-[1] inline-flex min-w-[20px] shrink-0 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold shadow-[0_6px_12px_rgba(49,82,230,0.18)]',
              'bg-primary text-white',
              !showLabels && !asChild && 'h-2.5 min-w-0 p-0'
            )}
            aria-label={`${unreadCount} unread notifications`}
          >
            {showLabels || asChild ? (unreadCount > 99 ? '99+' : unreadCount) : null}
          </span>
        )}
      </NavLink>
    )
  }

  // One page: no dropdown, just the page itself.
  if (section.items.length === 1) return renderLink(section.items[0], false)

  if (!showLabels) {
    return (
      <div onMouseEnter={openFlyout} onMouseLeave={closeFlyoutSoon}>
        <NavLink
          ref={triggerRef}
          to={section.items[0].to}
          title={section.label}
          aria-haspopup="menu"
          onFocus={openFlyout}
          onBlur={closeFlyoutSoon}
          className={cn(baseLinkClass, containsActive ? activeClass : inactiveClass)}
        >
          <MenuIcon className="relative z-[1] h-[18px] w-[18px] shrink-0" />
        </NavLink>
        {flyoutTop !== null &&
          createPortal(
            <div
              role="menu"
              aria-label={section.label}
              onMouseEnter={openFlyout}
              onMouseLeave={closeFlyoutSoon}
              onFocus={openFlyout}
              onBlur={closeFlyoutSoon}
              style={{ top: flyoutTop, left: flyoutLeft }}
              className="fixed z-[60] w-52 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl dark:border-gray-700 dark:bg-gray-900"
            >
              <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => renderLink(item, true))}
              </div>
            </div>,
            document.body
          )}
      </div>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={submenuId}
        className={cn(
          baseLinkClass,
          'w-full text-left',
          containsActive && !isOpen ? activeClass : inactiveClass,
          containsActive && 'text-primary dark:text-primary-light'
        )}
      >
        <MenuIcon className="relative z-[1] h-[18px] w-[18px] shrink-0" />
        <span className="min-w-0 flex-1 overflow-hidden whitespace-nowrap">{section.label}</span>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
        )}
      </button>
      {isOpen && (
        <div
          id={submenuId}
          className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-2 dark:border-gray-600"
        >
          {section.items.map((item) => renderLink(item, true))}
        </div>
      )}
    </div>
  )
}

SidebarNavMenu.displayName = 'SidebarNavMenu'
